import React, { useRef, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { API } from "@/config/api";

// Pages
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import DoctorsPage from "@/pages/DoctorsPage";
import VisitsPage from "@/pages/VisitsPage";
import RepTerritoryPage from "@/pages/RepTerritoryPage";
import ManagerRepTerritoryPage from "@/pages/ManagerRepTerritoryPage";
import PipelinePage from "@/pages/PipelinePage";
import CalendarPage from "@/pages/CalendarPage";
import RepCalendarPage from "@/pages/RepCalendarPage";
import UpcomingPage from "@/pages/UpcomingPage";
import CaseDetailPage from "@/pages/CaseDetailPage";
import ReportsPage from "@/pages/ReportsPage";
import RepReportsPage from "@/pages/RepReportsPage";
import EodPage from "@/pages/EodPage";
import RepHomePage from "@/pages/RepHomePage";
import ExportPage from "@/pages/ExportPage";
import MedicalAidsPage from "@/pages/MedicalAidsPage";
import InsightsPage from "@/pages/InsightsPage";
import EodSnapshotsPage from "@/pages/EodSnapshotsPage";
import PipelineReportsInsightsPage from "@/pages/PipelineReportsInsightsPage";
import DoctorTimelinePage from "@/pages/DoctorTimelinePage";
import SmartReportsPage from "@/pages/SmartReportsPage";
import Layout from "@/components/Layout";
import { RealtimeProvider } from "@/context/RealtimeContext";

// Configure axios for same-origin requests (no CORS issues)
// Using relative URLs means requests go to the same domain
axios.defaults.withCredentials = true;

// Simple request logging for debugging
axios.interceptors.request.use(request => {
  console.log('[API]', request.method?.toUpperCase(), request.url);
  return request;
});

// Response interceptor for auth errors
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      console.log('[API] 401 - Not authenticated');
    }
    return Promise.reject(error);
  }
);

// Auth Callback Component
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.replace("#", "?")).get("session_id");
      
      if (!sessionId) {
        navigate("/", { replace: true });
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, {
          withCredentials: true
        });
        
        // Mobile browsers need a delay for cookie to be properly set before navigation
        // This is a known issue with cross-site cookies on iOS Safari and Android Chrome
        const isMobile = /iPad|iPhone|iPod|Android/i.test(navigator.userAgent) || 
                         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        
        if (isMobile) {
          await new Promise(resolve => setTimeout(resolve, 800));
        }
        
        const loggedInUser = response.data;
        if (loggedInUser?.role === "rep") {
          navigate("/rep-home", { replace: true, state: { user: loggedInUser } });
        } else {
          navigate("/dashboard", { replace: true, state: { user: loggedInUser } });
        }
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const retryCount = useRef(0);
  const maxRetries = 3;

  useEffect(() => {
    // If user was passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        setIsAuthenticated(true);
        retryCount.current = 0;
      } catch (error) {
        // Mobile browsers (iOS/Android) may need retries due to cookie timing issues
        const isMobile = /iPad|iPhone|iPod|Android/i.test(navigator.userAgent) || 
                         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        
        if (isMobile && retryCount.current < maxRetries) {
          retryCount.current += 1;
          console.log(`Mobile auth retry ${retryCount.current}/${maxRetries}`);
          // Exponential backoff: 500ms, 1000ms, 2000ms
          setTimeout(checkAuth, 500 * Math.pow(2, retryCount.current - 1));
          return;
        }
        
        setIsAuthenticated(false);
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return React.cloneElement(children, { user });
};

// Not Authorized Page Component
const NotAuthorizedPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8 max-w-md">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
        <p className="text-muted-foreground mb-6">
          This feature is only available to managers and administrators.
        </p>
        <button
          onClick={() => navigate("/visits")}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          Go to My Visits
        </button>
      </div>
    </div>
  );
};

// Manager-Only Route Guard Component
const ManagerRoute = ({ children, user }) => {
  // If user is a rep, show not authorized page
  if (user?.role === "rep") {
    return <NotAuthorizedPage />;
  }
  // Pass user down to children
  return React.cloneElement(children, { user });
};

// App Router
function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              {/* Dashboard is manager-only */}
              <ManagerRoute><Dashboard /></ManagerRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/doctors"
        element={
          <ProtectedRoute>
            <Layout><DoctorsPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rep-home"
        element={
          <ProtectedRoute>
            <Layout><RepHomePage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rep/territories"
        element={
          <ProtectedRoute>
            <Layout><RepTerritoryPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/rep-territory"
        element={
          <ProtectedRoute>
            <Layout><ManagerRoute><ManagerRepTerritoryPage /></ManagerRoute></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/rep-territory/:repName"
        element={
          <ProtectedRoute>
            <Layout><ManagerRoute><ManagerRepTerritoryPage /></ManagerRoute></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits"
        element={
          <ProtectedRoute>
            <Layout><VisitsPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <Layout>
              {/* Pipeline is manager-only */}
              <ManagerRoute><PipelinePage /></ManagerRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rep-calendar"
        element={
          <ProtectedRoute>
            <Layout><RepCalendarPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/upcoming"
        element={
          <ProtectedRoute>
            <Layout><UpcomingPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <Layout>
              {/* Referral Calendar is manager-only */}
              <ManagerRoute><CalendarPage /></ManagerRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/:tinNumber"
        element={
          <ProtectedRoute>
            <Layout>
              {/* Case detail is manager-only */}
              <ManagerRoute><CaseDetailPage /></ManagerRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Layout><RepReportsPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eod"
        element={
          <ProtectedRoute>
            <Layout><EodPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rep-reports"
        element={<Navigate to="/reports" replace />}
      />
      <Route
        path="/exports"
        element={
          <ProtectedRoute>
            <Layout>
              {/* Exports page is manager-only */}
              <ManagerRoute><ExportPage /></ManagerRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/medical-aids"
        element={
          <ProtectedRoute>
            <Layout>
              <ManagerRoute><MedicalAidsPage /></ManagerRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Admin page removed - redirect to dashboard */}
      <Route
        path="/insights"
        element={
          <ProtectedRoute>
            <Layout><ManagerRoute><InsightsPage /></ManagerRoute></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eod-snapshots"
        element={
          <ProtectedRoute>
            <Layout><ManagerRoute><EodSnapshotsPage /></ManagerRoute></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline-reports"
        element={
          <ProtectedRoute>
            <Layout><ManagerRoute><PipelineReportsInsightsPage /></ManagerRoute></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/smart-reports"
        element={
          <ProtectedRoute>
            <Layout><ManagerRoute><SmartReportsPage /></ManagerRoute></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/doctor-timeline"
        element={
          <ProtectedRoute>
            <Layout><ManagerRoute><DoctorTimelinePage /></ManagerRoute></Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
      <Route path="/admin-tools" element={<Navigate to="/dashboard" replace />} />
      <Route path="/settings/admin" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
      <RealtimeProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </RealtimeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
